import type { Resolver } from '../extensionTypes.ts';

type PrefixSetting = {
	prefix: string;
	labelFunc: (labelExcludingPrefix: string) => string;
};
type Options = {
	resolver: Resolver;
	prefixSettings: PrefixSetting[];
};

const defaultPrefixSetting = {
	prefix: '',
	labelFunc: (val) => val,
};

/*
 * Some macros allows specifying values (like labels or spaces) with a prefix,
 * which causes the value to have a slightly different meaning.
 * For example, the "Blog Posts" macro could have a labels value like this:
 * ["apple", "banana", "+orange", "-grapefruit"]
 * This means:
 * - Find blog posts with the labels "apple" OR "banana".
 * - Of those, only include ones with the label "orange".
 * - Remove any blog posts with the label "grapefruit".
 *
 * This helper function allows you to modify a resolver to support prefixes like these..
 *
 * Usage:
 * ```
 * customPrefixResolver({
 *   resolver: labelResolver
 *   prefixSettings: [
 *     {
 *       prefix: "+",
 *       labelFunc: (labelExcludingPrefix) => `${labelExcludingPrefix} (required)`,
 *     },
 *     {
 *       prefix: "-",
 *       labelFunc: (labelExcludingPrefix) => `${labelExcludingPrefix} (excluded)`,
 *     }
 *   ]
 * })
 * ```
 */
export const customPrefixResolver = ({ resolver, prefixSettings }: Options): Resolver => {
	// Returns a prefix if `val` starts with a prefix, or `undefined` otherwise.
	const getPrefixSetting = (val: string | undefined): PrefixSetting | undefined => {
		for (const setting of prefixSettings) {
			if (val?.startsWith(setting.prefix)) {
				return setting;
			}
		}
		return undefined;
	};
	// Removes a prefix from `val` if it starts with one.
	const removePrefix = (val: string): string => {
		const prefix = getPrefixSetting(val)?.prefix;
		if (prefix) {
			// Remove the `prefix` from `val`.
			return val.slice(prefix.length);
		}
		return val;
	};

	// Known issue: If a value has a prefix when the page is loaded, it is stuck in the `selectedValue` array forever.
	// This makes it impossible to switch a value from one prefix to another, or to remove the prefix,
	// without removing the value, reloading the page, and adding it again.
	return async (searchTerm?: string, selectedValue?: string | string[]) => {
		// Create a `preprocessedSelectedValue` array without prefixes so the resolver gives accurate reccommendations.
		let preprocessedSelectedValue = selectedValue;
		// A map from prefix-less labels to the PrefixSetting object that matches with the prefix they started with.
		const labelToPrefixSetting = {};
		// Depending on the resolver, `selectedValue` can be an array of strings, or just a string.  This handles both cases.
		if (Array.isArray(selectedValue)) {
			for (const selVal of selectedValue) {
				const selValWithoutPrefix = removePrefix(selVal);
				labelToPrefixSetting[selValWithoutPrefix] = getPrefixSetting(selVal);
			}
			preprocessedSelectedValue = selectedValue.map(removePrefix);
		} else if (selectedValue) {
			const selValWithoutPrefix = removePrefix(selectedValue);
			labelToPrefixSetting[selValWithoutPrefix] = getPrefixSetting(selectedValue);
			preprocessedSelectedValue = selValWithoutPrefix;
		}

		// Remove the prefix from the searchTerm, and save the PrefixSetting corresponding with its prefix.
		const searchTermPrefixSetting = getPrefixSetting(searchTerm) ?? defaultPrefixSetting;
		if (searchTerm) {
			searchTerm = removePrefix(searchTerm);
		}

		// Call the resolver with normal, prefix-less arguments.
		const results = await resolver(searchTerm, preprocessedSelectedValue);

		// Add prefixes back on to the results of the resolver.
		return results.map((result) => {
			// Default to using no prefix.
			let prefixSetting = defaultPrefixSetting;
			if (searchTerm) {
				// If a search is happening, use the searchTerm's prefix.
				prefixSetting = searchTermPrefixSetting;
			} else if (result.value in labelToPrefixSetting) {
				// When no search is happening, use the prefixes from the selectedValue array.
				// This allows results to render correctly on initial load.
				prefixSetting = labelToPrefixSetting[result.value] ?? defaultPrefixSetting;
			}

			return {
				...result,
				// Use the labelFunc to modify the result's label.
				// This displays prefixes in a more user-friendly way
				label: prefixSetting.labelFunc(removePrefix(result.label)),
				// Add the prefix back on to the result's value.
				value: `${prefixSetting.prefix}${result.value}`,
			};
		});
	};
};
